// src/components/Labs/biology/BiologyExperimentView.jsx
import React, { useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import BioPopup from "./components/BioPopup";
import BioStepProgress from "./components/BioStepProgress";
import BioDropZone from "./components/BioDropZone";
import BioReactionLog from "./components/BioReactionLog";
import SpecimenBottle from "./components/SpecimenBottle";

import MicroscopeOverlay from "./apparatus/MicroscopeOverlay";
import PetriDishBase from "./apparatus/PetriDishBase";
import SlideView from "./apparatus/SlideView";

export default function BiologyExperimentView({
  experiment,
  selectedClass,
  onBack,
  onComplete,
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [loadedSpecimenId, setLoadedSpecimenId] = useState(null);
  const [log, setLog] = useState([]);
  const [popup, setPopup] = useState(null);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    setCurrentStep(0);
    setLoadedSpecimenId(null);
    setLog([]);
    setCompleted(false);
    setPopup(
      experiment?.steps?.length
        ? { type: "info", message: experiment.steps[0] }
        : null
    );
  }, [experiment?.id]);

  if (!experiment) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 font-semibold">Experiment not found.</p>
        <button onClick={onBack} className="mt-3 text-blue-600 underline">
          ← Back
        </button>
      </div>
    );
  }

  const handleCorrectDrop = (specimen) => {
    setLoadedSpecimenId(specimen.id);

    if (experiment.observation) {
      setLog((prev) => [...prev, experiment.observation]);
    }

    setPopup({
      type: "success",
      message: experiment.successMessage || "Correct specimen loaded.",
    });

    setCurrentStep((prev) =>
      experiment.steps && experiment.steps.length > 0
        ? Math.min(prev + 1, experiment.steps.length - 1)
        : prev
    );

    if (!completed && onComplete) {
      setCompleted(true);
      onComplete(experiment.id);
    }
  };

  const handleSpecimenDrop = (item) => {
    if (!item?.id) return;

    const specimen = experiment.specimens.find((s) => s.id === item.id);
    if (!specimen) return;

    if (item.id !== experiment.correctSpecimenId) {
      setPopup({
        type: "error",
        message: `Incorrect specimen (${specimen.name}). Try again.`,
      });
      setLog((prev) => [...prev, `Wrong specimen used: ${specimen.name}`]);
      return;
    }

    handleCorrectDrop(specimen);
  };

  const loadedSpecimen = experiment.specimens.find(
    (s) => s.id === loadedSpecimenId
  );

  const apparatusProps = {
    loaded: !!loadedSpecimen,
    specimenName: loadedSpecimen?.name,
    specimenId: loadedSpecimen?.id,
    image: loadedSpecimen?.image,
    onSpecimenDrop: handleSpecimenDrop, 
  };

  const renderApparatus = () => {
    switch (experiment.apparatus) {
      case "slide":
        return <SlideView {...apparatusProps} />;
      case "petri":
        return <PetriDishBase {...apparatusProps} />;
      case "microscope":
      default:
        return <MicroscopeOverlay {...apparatusProps} />;
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="min-h-[calc(100vh-120px)] flex flex-col md:flex-row bg-gradient-to-br from-pink-50 to-purple-100">
        {/* Sidebar */}
        <aside className="md:w-72 w-full bg-white/90 border-b md:border-r border-gray-200 p-5 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="text-sm text-pink-700 font-medium hover:underline"
            >
              ← Back
            </button>
            <span className="text-[11px] text-gray-500">
              Class {selectedClass || experiment.classLevel || "—"}
            </span>
          </div>

          <h2 className="font-bold text-lg text-pink-700">
            {experiment.title}
          </h2>
          <p className="text-xs text-gray-600">{experiment.description}</p>

          <BioStepProgress steps={experiment.steps || []} current={currentStep} />

          {/* Specimens */}
          {experiment.specimens?.length > 0 && (
            <div className="mt-3">
              <h3 className="text-gray-700 font-semibold mb-1">🧫 Specimens</h3>
              <div className="grid grid-cols-2 gap-3">
                {experiment.specimens.map((s) => (
                  <SpecimenBottle key={s.id} specimen={s} disabled={false} />
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* Main View */}
        <main className="flex-1 flex flex-col items-center justify-center p-4 relative">
          <div className="absolute top-4 left-1/2 -translate-x-1/2 w-[90%] max-w-md">
            <BioPopup popup={popup} />
          </div>

          <div className="flex flex-col items-center gap-4 mt-10">
            {/* Apparatus itself is also a drop zone */}
            {renderApparatus()}

            {/* Additional drop zone below apparatus (Option C) */}
            <BioDropZone
              label={experiment.actionLabel || "Drop the correct specimen here"}
              onDrop={handleSpecimenDrop}
            />

            <div className="w-full max-w-lg mt-4">
              <BioReactionLog log={log} />
            </div>
          </div>
        </main>
      </div>
    </DndProvider>
  );
}
